import { createSlice } from "@reduxjs/toolkit";
import { toastMsg } from "../../components/common/Toast/Toast";
import { startLoading, stopLoading } from "../loading/loadingSlice";
import { API } from "../../services/apiService";
import { isUserLogin, saveChangesGridLayout, setPairName, setUesrInfo } from "../persist/persistSlice";
import storage from "redux-persist/lib/storage";
import { parseJSON } from "../../helpers/commonHelper";
import { isApiSuccessResponse } from "../../services/order/orderApi.utils";
import { buildAssetListParams } from "../../constants/assetType.constants";
import {
  persistSocketAccessToken,
  syncSocketConnection,
} from "../../services/socketAuth";
import { normalizeLinkedWalletsResponse, enrichBalanceWithPairWallet } from "../../services/wallet/linkedWallet.utils";
import { NON_CUSTODIAL_CONNECTION_STATUS } from "../../constants/wallet.constants";
import { socket } from "../../socket/client";

const extractAccessToken = (response) => {
    const possibleToken = response?.data?.data?.token
        || response?.data?.data?.accessToken
        || response?.data?.data?.access_token
        || response?.data?.token
        || response?.data?.accessToken
        || response?.data?.access_token
        || response?.token
        || response?.accessToken
        || response?.access_token;

    if (typeof possibleToken === "string" && possibleToken.trim()) {
        return possibleToken.trim();
    }

    return "";
};

const AUTH_STORAGE_KEYS = [
    "ats_local_auth_token",
    "user_access_cookies",
    "access_token",
    "accessToken",
    "token",
];

const clearClientAuthState = () => {
    if (typeof window === "undefined") return;
    persistSocketAccessToken("");
    AUTH_STORAGE_KEYS.forEach((key) => window.localStorage.removeItem(key));
};

const initialState = {
    pairList: [],
    selectedPair: null,
    buyOrderBook: [],
    sellOrderBook: [],
    tradeBook: [],
    lastTrade: null,
    lastPrice: 0,
    activePairBalance: null,
    report: null,
    linkedWallets: [],
    linkedWalletsLoading: false,
    selectedTradeWallet: null,
    nonCustodialConnectionStatus: NON_CUSTODIAL_CONNECTION_STATUS.IDLE,
};
export const atsSlice = createSlice({
    name: "ats",
    initialState,
    reducers: {
        setPairList: (state, action) => {
            state.pairList = action.payload;
        },
        setSelectedPair: (state, action) => {
            state.selectedPair = action.payload;
        },
        setBuyOrderBook: (state, action) => {
            state.buyOrderBook = action.payload;
        },
        setSellOrderBook: (state, action) => {
            state.sellOrderBook = action.payload;
        },
        setTradeBook: (state, action) => {
            state.tradeBook = action.payload;
        },
        setLastTrade: (state, action) => {
            state.lastTrade = action.payload;
        },
        setLastPrice: (state, action) => {
            state.lastPrice = action.payload;
        },
        setActivePairBalance: (state, action) => {
            state.activePairBalance = action.payload;
        },
        setReport: (state, action) => {
            state.report = action.payload;
        },
        setLinkedWallets: (state, action) => {
            state.linkedWallets = action.payload;
        },
        setLinkedWalletsLoading: (state, action) => {
            state.linkedWalletsLoading = action.payload;
        },
        setSelectedTradeWallet: (state, action) => {
            state.selectedTradeWallet = action.payload;
        },
        clearSelectedTradeWallet: (state) => {
            state.selectedTradeWallet = null;
            state.nonCustodialConnectionStatus = NON_CUSTODIAL_CONNECTION_STATUS.IDLE;
        },
        setNonCustodialConnectionStatus: (state, action) => {
            state.nonCustodialConnectionStatus = action.payload;
        },
    },
});
export const {
    setPairList,
    setSelectedPair,
    setBuyOrderBook,
    setSellOrderBook,
    setTradeBook,
    setLastTrade,
    setLastPrice,
    setActivePairBalance,
    setReport,
    setLinkedWallets,
    setLinkedWalletsLoading,
    setSelectedTradeWallet,
    clearSelectedTradeWallet,
    setNonCustodialConnectionStatus,
} = atsSlice.actions;
export default atsSlice.reducer;
const fetchPairList = pair => async (dispatch) => {
    return new Promise(async (resolve, reject) => {
        try {
            dispatch(startLoading());
            const response = await API.fetchPairList();
            dispatch(setPairList(response.data.pairList));
            if (response.data.pairList.length) {
                if (window.location.pathname === '/trade') {
                    setPair(pair, response.data.pairList, dispatch);
                } else {
                    dispatch(setPairName(response.data.pairList[0].pair_key))
                    dispatch(setSelectedPair(parseJSON(response.data.pairList[0])))
                }
            }
            resolve(response.data.data);
            dispatch(stopLoading());
        } catch (error) {
            toastMsg.error(error?.response?.data?.message);
            dispatch(stopLoading());
        }
    });
};
const placeOrder = (body) => (dispatch) => {
    return new Promise(async (resolve) => {
        try {
            const response = await API.createOrder(body);
            if (isApiSuccessResponse(response)) {
                toastMsg.success(response.message || 'Order placed successfully.');
            }
            resolve(response);
        } catch (error) {
            const errorResponse = error?.response
                ? {
                    ...error.response,
                    message: error?.message,
                    code: error?.code,
                  }
                : {
                    status: error?.response?.status ?? 500,
                    data: error?.response?.data,
                    message: error?.message,
                    code: error?.code,
                  };
            resolve(errorResponse);
        }
    });
};
const fetchBuySellOrderBook = (body) => (dispatch) => {
    return new Promise(async (resolve, reject) => {
        try {
            // dispatch(startLoading());
            const response = await API.fetchOrderBooks(body);
            dispatch(setBuyOrderBook(response.data.buyOrderBook));
            dispatch(setSellOrderBook(response.data.sellOrderBook?.reverse()));
            resolve(response);
            // dispatch(stopLoading());
        } catch (error) {
            toastMsg.error(error?.response?.data?.message);
            // dispatch(stopLoading());
        }
    });
};
const fetchMarketTrade = (body) => (dispatch) => {
    return new Promise(async (resolve, reject) => {
        try {
            // dispatch(startLoading());
            const response = await API.fetchMarketTrade(body);
            dispatch(setTradeBook(response.data.tradeList));
            resolve(response);
            // dispatch(stopLoading());
        } catch (error) {
            toastMsg.error(error?.response?.data?.message);
            resolve(error?.response ?? { status: error?.response?.status ?? 500 });
            // dispatch(stopLoading());
        }
    });
};
const loginAction = (body) => (dispatch) => {
    return new Promise(async (resolve, reject) => {
        try {
            dispatch(startLoading());
            const response = await API.login(body);
            console.log("Login Response -> ", response);
            if (response.status === 200) {
                const accessToken = extractAccessToken(response);
                persistSocketAccessToken(accessToken);
                dispatch(setPairName(response.data.pair))
                dispatch(isUserLogin(true));
                syncSocketConnection(socket);
            }
            resolve(response);
            dispatch(stopLoading());
        } catch (error) {
            toastMsg.error(error?.response?.data?.message);
            dispatch(stopLoading());
        }
    });
};
const logout = () => (dispatch) => {
    return new Promise(async (resolve) => {
        dispatch(startLoading());
        const finishLocalLogout = async () => {
            clearClientAuthState();
            dispatch(isUserLogin(false));
            dispatch(setPairName(""));
            dispatch(setUesrInfo(null));
            try {
                await storage.removeItem("persist:root");
            } catch {
                /* ignore */
            }
        };
        try {
            const response = await API.logout();
            await finishLocalLogout();
            dispatch(stopLoading());
            if (Number(response?.status) === 200) {
                window.location.href = "/auth/login";
            }
            resolve(response);
        } catch (error) {
            await finishLocalLogout();
            dispatch(stopLoading());
            toastMsg.error(
                error?.response?.data?.message || "Logout failed. Signed out locally.",
            );
            window.location.href = "/auth/login";
            resolve(error?.response ?? { status: error?.response?.status ?? 500 });
        }
    });
};
const getNotifications = (body) => {
    return new Promise(async (resolve, reject) => {
        try {
            const response = await API.fetchNotifications(body.offset, body.limit);
            resolve(response);
        } catch (error) {
            toastMsg.error(error?.response?.data?.message);
            reject(error);
        }
    });
};

const markNotificationsRead = (body) => {
    return new Promise(async (resolve, reject) => {
        try {
            const response = await API.markNotificationsRead(body);
            resolve(response);
        } catch (error) {
            toastMsg.error(error?.response?.data?.message);
            reject(error);
        }
    });
};
const fetchLinkedWallets = (symbol) => async (dispatch) => {
    return new Promise(async (resolve) => {
        const normalizedSymbol = String(symbol ?? "").trim();
        if (!normalizedSymbol) {
            dispatch(setLinkedWallets([]));
            resolve({ status: 400, data: { wallets: [] } });
            return;
        }

        dispatch(setLinkedWalletsLoading(true));
        try {
            const response = await API.fetchLinkedWallets(normalizedSymbol);
            const { wallets } = normalizeLinkedWalletsResponse(response);
            dispatch(setLinkedWallets(wallets));
            resolve(response);
        } catch (error) {
            dispatch(setLinkedWallets([]));
            toastMsg.error(error?.response?.data?.message || "Failed to load linked wallets.");
            resolve(error?.response ?? { status: error?.response?.status ?? 500 });
        } finally {
            dispatch(setLinkedWalletsLoading(false));
        }
    });
};

const fetchUserBalance = (body) => async (dispatch, getState) => {
    return new Promise(async (resolve, reject) => {
        try {
            // dispatch(startLoading());
            const response = await API.fetchActivePairBalance(body);
            const state = getState()?.ats ?? {};
            const selectedPair = {
                baseCurrency: body.baseCurrency,
                otherCurrency: body.otherCurrency,
            };
            const linkedWallets = Array.isArray(state.linkedWallets)
                ? state.linkedWallets
                : state.linkedWallets?.wallets ?? [];
            const enriched = enrichBalanceWithPairWallet(
                response.data,
                selectedPair,
                state.selectedTradeWallet,
                linkedWallets,
            );
            dispatch(setActivePairBalance(enriched));
            resolve(response);
            // dispatch(stopLoading());
        } catch (error) {
            toastMsg.error(error?.response?.data?.message);
            resolve(error?.response);
            // dispatch(stopLoading());
        }
    });
};
const fetchUserOrderList = (body) => {
    return new Promise(async (resolve, reject) => {
        try {
            const response = await API.fetchUserOrderList(body);
            resolve(response);
        } catch (error) {
            resolve(error.response)
            toastMsg.error(error?.response?.data?.message);
        }
    });
};
const fetchUserOrderHistory = (body) => {
    return new Promise(async (resolve, reject) => {
        try {
            const response = await API.getOrderHistory(body);
            resolve(response);
        } catch (error) {
            resolve(error.response)
            toastMsg.error(error?.response?.data?.message);
        }
    });
};
const cancelUserOrder = (body) => {
    return new Promise(async (resolve, reject) => {
        try {
            const response = await API.cancelOrder(body);
            toastMsg.success(response.message);
            resolve(response);
        } catch (error) {
            resolve(error?.response)
            toastMsg.error(error?.response?.data?.message);
        }
    });
};
const getUserTrades = (body) => {
    return new Promise(async (resolve, reject) => {
        try {
            const response = await API.fetchUserTrades(body);
            resolve(response);
        } catch (error) {
            resolve(error?.response)
            toastMsg.error(error?.response?.data?.message);
        }
    });
};
const setPair = (pair, pairList, dispatch) => {
    if (pair) {
        const index = pairList.findIndex(o => o.pair_key.toLowerCase() === pair.toLowerCase());
        // const index = pairList.findIndex(o => o.name.toLowerCase() === pair.toLowerCase());
        if (index > -1) {
            dispatch(setPairName(pairList[index].pair_key))
            dispatch(setSelectedPair(parseJSON(pairList[index])))
        } else {
            dispatch(setPairName(pairList[0].pair_key))
            dispatch(setSelectedPair(parseJSON(pairList[0])))
        }
    } else {
        const urlParams = new URLSearchParams(window.location.search);
        urlParams.set('symbol', pairList[0].pair_key.replace('/', '_').toLowerCase());
        window.location.search = urlParams;
        dispatch(setSelectedPair(parseJSON(pairList[0])))
    }
}

const fetchWalletList = (query = {}, options = {}) => async (dispatch) => {
    const { skipGlobalLoader = false } = options;
    return new Promise(async (resolve) => {
        if (!skipGlobalLoader) dispatch(startLoading());
        try {
            const response = await API.getWalletList(query);
            resolve(response);
        } catch (error) {
            toastMsg.error(error?.response?.data?.message);
            resolve(
                error?.response?.data ?? { status: error?.response?.status ?? 500 },
            );
        } finally {
            if (!skipGlobalLoader) dispatch(stopLoading());
        }
    });
};

const fetchWalletAssetList = (param) => async (dispatch) => {
    return new Promise(async (resolve, reject) => {
        try {
            dispatch(startLoading());
            const response = await API.getWalletAssetList({}, param);
            resolve(response)
            dispatch(stopLoading());
        } catch (error) {
            toastMsg.error(error?.response?.data?.message);
            dispatch(stopLoading());
        }
    });
};

/** Fetch assets by header asset-type filter (no UI binding yet). */
const fetchAssetListByType = (assetTypeValue) => async () => {
    return new Promise(async (resolve) => {
        try {
            const params = buildAssetListParams(assetTypeValue);
            const response = await API.getWalletAssetList({}, params);
            resolve(response);
        } catch (error) {
            console.error("fetchAssetListByType:", error?.response?.data?.message);
            resolve(
                error?.response?.data ?? { status: error?.response?.status ?? 500 },
            );
        }
    });
};

const fetchPortfolioBalanceGraph = (params) => async () => {
    return new Promise(async (resolve) => {
        try {
            const response = await API.fetchPortfolioBalanceGraph(params);
            resolve(response);
        } catch (error) {
            toastMsg.error(error?.response?.data?.message);
            resolve(
                error?.response?.data ?? { status: error?.response?.status }
            );
        }
    });
};

const fetchWalletHistory =
    (fetcher, query = {}, options = {}) =>
    async (dispatch) => {
        const { skipGlobalLoader = true } = options;
        return new Promise(async (resolve) => {
            if (!skipGlobalLoader) dispatch(startLoading());
            try {
                const response = await fetcher(query);
                resolve(response);
            } catch (error) {
                toastMsg.error(error?.response?.data?.message);
                resolve(
                    error?.response?.data ?? { status: error?.response?.status ?? 500 },
                );
            } finally {
                if (!skipGlobalLoader) dispatch(stopLoading());
            }
        });
    };

const fetchDepositHistory = (query, options) =>
    fetchWalletHistory(API.fetchDepositHistory, query, options);

const fetchWithdrawHistory = (query, options) =>
    fetchWalletHistory(API.fetchWithdrawHistory, query, options);

const fetchAssetAllocation = (params) => async () => {
    return new Promise(async (resolve) => {
        try {
            const response = await API.fetchAssetAllocation(params);
            resolve(response);
        } catch (error) {
            toastMsg.error(error?.response?.data?.message);
            resolve(
                error?.response?.data ?? { status: error?.response?.status }
            );
        }
    });
};


const updateWalletStatus = (body) => async (dispatch) => {
    return new Promise(async (resolve, reject) => {
        try {
            dispatch(startLoading());
            const response = await API.updateWalletStatus(body);
            console.log("ddd", response);
            resolve(response)
            dispatch(stopLoading());
        } catch (error) {
            console.log("EROR", error)
            toastMsg.error(error?.response?.data?.message);
            dispatch(stopLoading());
        }
    });
};
const addAlert = async (body) => {
    return new Promise(async (resolve, reject) => {
        try {
            const response = await API.createAlert(body);
            toastMsg.success(response.message);
            resolve(response);
        } catch (error) {
            console.log("EROR", error)
            toastMsg.error(error?.response?.data?.message);
            resolve(error?.response?.data);
        }
    })
}
const fetchAlerts = async (body) => {
    return new Promise(async (resolve, reject) => {
        try {
            const response = await API.fetchAlerts(body);
            resolve(response);
        } catch (error) {
            console.log("EROR", error)
            toastMsg.error(error?.response?.data?.message);
            reject(error?.response?.data);
        }
    })
}
const cancelAlert = async (body) => {
    return new Promise(async (resolve, reject) => {
        try {
            const response = await API.cancelAlert(body);
            resolve(response);
        } catch (error) {
            console.log("EROR", error)
            toastMsg.error(error?.response?.data?.message);
            resolve(error?.response?.data);
        }
    })
}
const updateAlert = async (body) => {
    return new Promise(async (resolve, reject) => {
        try {
            const response = await API.editAlert(body);
            toastMsg.success(response.message);
            resolve(response);
        } catch (error) {
            console.log("EROR", error)
            toastMsg.error(error?.response?.data?.message);
            resolve(error?.response?.data);
        }
    })
}
const getGasPrice = async (body) => {
    return new Promise(async (resolve, reject) => {
        try {
            const response = await API.fetchGasPrice();
            resolve(response);
        } catch (error) {
            console.log("EROR", error)
            toastMsg.error(error?.response?.data?.message);
            resolve(error?.response?.data);
        }
    })
}
const getEstimatedGasFee = async (coin, walletType, amount) => {
    return new Promise(async (resolve, reject) => {
        try {
            const response = await API.fetchEstimatedGasFee(coin, walletType, amount);
            resolve(response);
        } catch (error) {
            toastMsg.error(error?.response?.data?.message);
            resolve(error?.response?.data);
        }
    })
}
const editUserInfo = body => dispatch => {
    return new Promise(async (resolve, reject) => {
        try {
            dispatch(startLoading());
            const response = await API.editUserInfo(body);
            toastMsg.success(response.message);
            resolve(response);
            dispatch(stopLoading());
        } catch (error) {
            console.log("EROR", error)
            toastMsg.error(error?.response?.data?.message);
            resolve(error?.response?.data);
            dispatch(stopLoading());
        }
    })
}
const fetchReport = pairName => dispatch => {
    return new Promise(async (resolve, reject) => {
        try {
            dispatch(startLoading());
            const response = await API.getReport({ pairName });
            dispatch(setReport(response.data));
            resolve(response);
            dispatch(stopLoading());
        } catch (error) {
            toastMsg.error(error?.response?.data?.message);
            resolve(error?.response ?? { status: error?.response?.status ?? 500 });
            dispatch(stopLoading());
        }
    })
}

export const ATS = {
    fetchPairList,
    placeOrder,
    fetchBuySellOrderBook,
    fetchMarketTrade,
    loginAction,
    logout,
    getNotifications,
    markNotificationsRead,
    fetchUserBalance,
    fetchLinkedWallets,
    fetchUserOrderList,
    fetchUserOrderHistory,
    cancelUserOrder,
    getUserTrades,
    fetchWalletList,
    fetchWalletAssetList,
    fetchAssetListByType,
    fetchPortfolioBalanceGraph,
    fetchAssetAllocation,
    fetchDepositHistory,
    fetchWithdrawHistory,
    updateWalletStatus,
    addAlert,
    fetchAlerts,
    cancelAlert,
    updateAlert,
    editUserInfo,
    getGasPrice,
    getEstimatedGasFee,
    fetchReport
};
